
// Socket manager for media socket.
import { MeetingRoomParams, RecordingParams } from '../@types/types';
import io, { Socket } from 'socket.io-client'; // Importing socket type
import {
  MediaSocketConnectError,
  MediaSocketConnectPhase,
  MediaConnectionRejectionReason,
  currentOnlineState,
  normalizeRejectionReason,
} from './mediaSocketErrors';

/**
 * After the handshake, Socket.IO reconnects this socket on its own whenever the transport drops. A media node that
 * refuses such a reconnect for good (the room ended, the credentials were replaced by a newer join, no meeting
 * minutes) would be retried for as long as the page stays open, so a permanent refusal closes the socket instead.
 * Transient refusals (`api-check-failed`) keep today's behaviour.
 */
export function stopReconnectingWhenRefused(socket: Socket): void {
  const onRejected = (payload?: { reason?: string; stage?: string }) => {
    const refusal = new MediaSocketConnectError({
      phase: 'rejected',
      reason: normalizeRejectionReason(payload?.reason),
      stage: typeof payload?.stage === 'string' ? payload.stage : undefined,
      elapsedMs: 0,
    });
    if (refusal.retryable) return;
    socket.off('connection-rejected', onRejected);
    console.warn(`MediaSFU media socket: reconnect refused (${refusal.reason}); not retrying.`);
    try {
      // With no active socket left on its manager, Socket.IO stops reconnecting.
      socket.disconnect();
    } catch {
      // Already closed.
    }
  };
  socket.on('connection-rejected', onRejected);
}

/**
 * Validates the provided API key or token.
 * @param {string} value - The API key or token to validate.
 * @returns {Promise<Boolean>} - True if the API key or token is valid, false otherwise.
 */
async function validateApiKeyToken(value: string): Promise<boolean> {
  // API key or token must be alphanumeric and length 64
  if (!/^[a-z0-9]{64}$/i.test(value)) {
    throw new Error('Invalid API key or token.');
  }
  return true;
}

export interface ResponseLocalConnection {
  socket?: Socket;
  data?: ResponseLocalConnectionData;
}

export interface ResponseLocalConnectionData {
  socketId: string;
  mode: string;
  apiUserName?: string;
  apiKey?: string;
  allowRecord: boolean;
  meetingRoomParams_: MeetingRoomParams;
  recordingParams_: RecordingParams;
}

export interface ConnectLocalSocketOptions {
  link: string;
}

export interface ConnectSocketOptions {
  apiUserName: string;
  apiKey?: string;
  apiToken?: string;
  link: string;
}

export interface DisconnectSocketOptions {
  socket: Socket;
}

// Export the type definition for the function
export type ConnectSocketType = (options: ConnectSocketOptions) => Promise<Socket>;
export type DisconnectSocketType = (options: DisconnectSocketOptions) => Promise<boolean>;
export type ConnectLocalSocketType = (options: ConnectLocalSocketOptions) => Promise<ResponseLocalConnection>;


/**
 * Connects to a media socket using the provided connection options.
 *
 * @param {ConnectSocketOptions} options - The connection options.
 * @param {string} options.apiUserName - The API username.
 * @param {string} [options.apiKey] - The API key (optional if apiToken is provided).
 * @param {string} [options.apiToken] - The API token (optional if apiKey is provided).
 * @param {string} options.link - The socket link.
 *
 * @returns {Promise<Socket>} A promise that resolves to the connected socket.
 * @throws {MediaSocketConnectError} When the connection fails. `phase` says where (`before-open`, `after-open`,
 *   `rejected`, `timeout`), `reason`/`stage` carry the server's `connection-rejected` verdict, `elapsedMs` and `online`
 *   help tell a device/network problem from a refusal, and `retryable` is false for rejections that cannot succeed
 *   with the same credentials. `describeMediaSocketError(error)` turns it into a user-facing sentence.
 *
 * @example
 * ```typescript
 * const options = {
 *   apiUserName: 'user123',
 *   apiKey: 'yourApiKeyHere',
 *   link: 'https://socketlink.com',
 * };
 *
 * try {
 *   const socket = await connectSocket(options);
 *   console.log('Connected to socket:', socket);
 * } catch (error) {
 *   console.error('Failed to connect to socket:', error);
 * }
 * ```
 */

/** How long to wait for `connection-success` before giving up. */
const CONNECT_TIMEOUT_MS = 12000;

async function connectSocket(
  { apiUserName, apiKey, apiToken, link }: ConnectSocketOptions,
): Promise<Socket> {
  // Validate inputs
  if (!apiUserName) {
    throw new Error('API username required.');
  }
  if (!(apiKey || apiToken)) {
    throw new Error('API key or token required.');
  }
  if (!link) {
    throw new Error('Socket link required.');
  }

  // Validate the API key or token
  let useKey = false;
  try {
    if (apiKey && apiKey.length === 64) {
      await validateApiKeyToken(apiKey);
      useKey = true;
    } else if (apiToken && apiToken.length === 64) {
      await validateApiKeyToken(apiToken);
      useKey = false;
    } else {
      throw new Error('Invalid API key or token format.');
    }
  } catch {
    throw new Error('Invalid API key or token.');
  }

  let socket: Socket;

  return new Promise((resolve, reject) => {
    // Connect to socket using the link provided
    if (useKey) {
      socket = io(`${link}/media`, {
        transports: ['websocket'],
        query: {
          apiUserName: apiUserName,
          apiKey: apiKey!,
        },
      });
    } else {
      socket = io(`${link}/media`, {
        transports: ['websocket'],
        query: {
          apiUserName: apiUserName,
          apiToken: apiToken!,
        },
      });
    }

    // Every path below runs at most once, clears handshake listeners/timers,
    // and closes failed clients so Socket.IO cannot keep reconnecting an
    // orphan that the room lifecycle never received.
    let settled = false;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();
    let transportOpened = false;
    let rejection: { reason: MediaConnectionRejectionReason; stage?: string } | null = null;
    const cleanupHandshake = () => {
      if (timeoutHandle) clearTimeout(timeoutHandle);
      socket.off('connection-success', handleConnectionSuccess);
      socket.off('connect_error', handleConnectError);
      socket.off('disconnect', handleDisconnectBeforeReady);
      socket.off('connect', handleTransportOpen);
      socket.off('connection-rejected', handleConnectionRejected);
    };
    const failure = (phase: MediaSocketConnectPhase, transportError?: string) =>
      new MediaSocketConnectError({
        phase: rejection ? 'rejected' : phase,
        reason: rejection?.reason,
        stage: rejection?.stage,
        elapsedMs: Date.now() - startedAt,
        online: currentOnlineState(),
        transportError,
        link,
      });
    const settle = (action: () => void) => {
      if (settled) return;
      settled = true;
      cleanupHandshake();
      action();
    };

    // Handle socket connection events
    const handleConnectionSuccess = ({ socketId }: { socketId: string }) => {
      //check if link contains mediasfu.com and contains more than one c
      let conn = 'media';
      try {
        if (link.includes('mediasfu.com') && (link.match(/c/g)?.length ?? 0) > 1) {
          conn = 'consume';
        }
      } catch {
        // do nothing
      }

      console.log(`Connected to ${conn} socket with ID: ${socketId}`);
      settle(() => {
        stopReconnectingWhenRefused(socket);
        resolve(socket);
      });
    };

    const handleConnectError = (error: Error) => {
      settle(() => {
        socket.disconnect();
        reject(failure(transportOpened ? 'after-open' : 'before-open', error?.message));
      });
    };

    const handleTransportOpen = () => {
      transportOpened = true;
    };

    // Media admission v2: the node says why before it closes the handshake. Socket.IO's own reconnection never
    // retries a server-side close, and settling here stops this attempt from being retried as a network error.
    const handleConnectionRejected = (payload?: { reason?: string; stage?: string }) => {
      rejection = {
        reason: normalizeRejectionReason(payload?.reason),
        stage: typeof payload?.stage === 'string' ? payload.stage : undefined,
      };
      settle(() => {
        try {
          socket.disconnect();
        } catch {
          // Already closing.
        }
        reject(failure('rejected'));
      });
    };

    // The transport can connect and then be dropped by the server before it
    // ever sends `connection-success` — a rejected room, an expired token, a
    // bad handshake. Without this the promise stayed pending forever, so the
    // caller's loading modal never came down and no error was ever surfaced.
    const handleDisconnectBeforeReady = (reason: string) => {
      settle(() => reject(failure('after-open', reason)));
    };

    socket.on('connection-success', handleConnectionSuccess);
    socket.on('connect_error', handleConnectError);
    socket.on('disconnect', handleDisconnectBeforeReady);
    socket.on('connect', handleTransportOpen);
    socket.on('connection-rejected', handleConnectionRejected);

    // Last resort, for the case where the socket neither connects, errors nor
    // disconnects. Nothing above guarantees a settle on its own.
    timeoutHandle = setTimeout(() => {
      settle(() => {
        try {
          socket.disconnect();
        } catch {
          // Nothing useful to do here.
        }
        reject(failure('timeout'));
      });
    }, CONNECT_TIMEOUT_MS);
  });
}


/**
 * Connects to a local media socket using the provided connection options.
 *
 * @param {ConnectLocalSocketOptions} options - The connection options.
 * @param {string} options.link - The socket link.
 *
 * @returns {Promise<ResponseLocalConnection>} A promise that resolves to the connected socket and data.
 *
 * @example
 * ```typescript
 * const options = {
 *   link: 'http://localhost:3000',
 * };
 *
 * try {
 *   const { socket, data } = await connectLocalSocket(options);
 *   console.log('Connected to socket:', socket, data);
 * } catch (error) {
 *   console.error('Failed to connect to socket:', error);
 * }
 * ```
 */

async function connectLocalSocket({ link }: ConnectLocalSocketOptions): Promise<ResponseLocalConnection> {
  if (!link) {
    throw new Error('Socket link required.');
  }

  let socket: Socket;

  return new Promise((resolve, reject) => {
    // Connect to socket using the link provided
    socket = io(`${link}/media`, {
      transports: ['websocket'],
    });
    const startedAt = Date.now();
    let transportOpened = false;
    socket.on('connect', () => {
      transportOpened = true;
    });


    // Handle socket connection events
    socket.on('connection-success', (data: ResponseLocalConnectionData) => {
      resolve({ socket, data });
    });

    socket.on('connect_error', (error: Error) => {
      reject(
        new MediaSocketConnectError({
          phase: transportOpened ? 'after-open' : 'before-open',
          elapsedMs: Date.now() - startedAt,
          online: currentOnlineState(),
          transportError: error?.message,
          link,
        })
      );
    });
  });
}

/**
 * Disconnects from the socket.
 *
 * @param {Socket} socket - The socket instance to disconnect.
 * @returns {Promise<boolean>} - A promise that resolves once the socket is disconnected.
 *
 * @example
 * ```typescript
 * const options = { socket: socketInstance };
 *
 * try {
 *   const isDisconnected = await disconnectSocket(options);
 *   console.log('Disconnected:', isDisconnected);
 * } catch (error) {
 *   console.error('Failed to disconnect:', error);
 * }
 * ```
 */

async function disconnectSocket({ socket }: DisconnectSocketOptions): Promise<boolean> {
  if (socket) {
    socket.disconnect();
  }
  return true;
}

export { connectSocket, disconnectSocket, connectLocalSocket };
